import React, { useEffect, useMemo, useState } from "react";
import {
  getReceipts,
  postReceipt,
  unpostReceipt,
  deleteReceipt,
  WarehouseReceiptDto,
} from "../../../api/warehouseReceipts";
import ReceiptModal from "../modals/ReceiptModal";

type SortKey = "id" | "date" | "supplier" | "items" | "total" | "posted";

const errText = (e: any, fallback: string) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join("\n") : m || fallback;
};

export default function ReceiptsTab() {
  const [items, setItems] = useState<WarehouseReceiptDto[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // по умолчанию: новые документы сверху (по дате, при равной дате по номеру)
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  async function load() {
    const res = await getReceipts();
    setItems(res ?? []);
  }

  useEffect(() => {
    load();
  }, []);

  const totalOf = (r: WarehouseReceiptDto) => r.items?.reduce((sum, it) => sum + it.quantity * it.price, 0) ?? 0;

  const sorted = useMemo(() => {
    const val = (r: WarehouseReceiptDto): number | string => {
      switch (sortKey) {
        case "id": return r.id;
        case "date": return new Date(r.date).getTime();
        case "supplier": return (r.supplier?.name ?? "").toLowerCase();
        case "items": return r.items?.length ?? 0;
        case "total": return totalOf(r);
        case "posted": return r.posted ? 1 : 0;
      }
    };
    const k = sortDir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x < y) return -1 * k;
      if (x > y) return 1 * k;
      return (a.id - b.id) * k;
    });
  }, [items, sortKey, sortDir]);

  function sortBy(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "supplier" ? "asc" : "desc"); }
  }

  const th = (key: SortKey, label: string) => (
    <th onClick={() => sortBy(key)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }} title="Нажмите, чтобы отсортировать">
      {label}
      <span style={{ marginLeft: 4, color: sortKey === key ? "#818cf8" : "#4b5563", fontSize: 11 }}>
        {sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </th>
  );

  function openCreate() {
    setEditingId(null);
    setModalOpen(true);
  }

  function openEdit(id: number) {
    setEditingId(id);
    setModalOpen(true);
  }

  async function handlePost(id: number) {
    try { await postReceipt(id); } catch (e: any) { alert(errText(e, "Не удалось провести документ")); }
    await load();
  }

  async function handleUnpost(id: number) {
    if (!window.confirm("Снять документ с проведения? Остатки на складе уменьшатся на количество из документа, после этого его можно будет исправить.")) return;
    try { await unpostReceipt(id); } catch (e: any) { alert(errText(e, "Не удалось снять с проведения (возможно, товар уже продан)")); }
    await load();
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Удалить документ?")) return;
    try { await deleteReceipt(id); } catch (e: any) { alert(errText(e, "Не удалось удалить документ")); }
    await load();
  }

  return (
    <div className="warehouse-receipts-tab">
      <div className="header-row">
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Приход — список документов
        </div>

        <button className="primary-btn" onClick={openCreate}>
          Создать приход
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="stock-table">
          <thead>
            <tr>
              {th("id", "№")}
              {th("date", "Дата")}
              {th("supplier", "Поставщик")}
              {th("items", "Позиции")}
              {th("total", "Сумма")}
              {th("posted", "Статус")}
              <th></th>
            </tr>
          </thead>

          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 28, color: "#6b7280" }}>Документов прихода нет</td></tr>
            )}
            {sorted.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td style={{ whiteSpace: "nowrap" }}>{new Date(r.date).toLocaleDateString("ru-RU")}</td>
                <td>{r.supplier?.name ?? "—"}</td>
                <td>{r.items?.length ?? 0}</td>
                <td>{totalOf(r).toFixed(2)}</td>

                <td>
                  {r.posted ? (
                    <div className="tag-in">Проведён</div>
                  ) : (
                    <div className="tag-out">Черновик</div>
                  )}
                </td>

                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="table-btn" onClick={() => openEdit(r.id)}>
                    {r.posted ? "Открыть" : "Изменить"}
                  </button>

                  {!r.posted && (
                    <button className="table-btn" onClick={() => handlePost(r.id)}>
                      Провести
                    </button>
                  )}

                  {r.posted && (
                    <button className="table-btn" onClick={() => handleUnpost(r.id)} title="Чтобы исправить документ, сначала снимите проведение">
                      Снять с проведения
                    </button>
                  )}

                  {!r.posted && (
                    <button className="table-btn" onClick={() => handleDelete(r.id)}>
                      Удалить
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <ReceiptModal
          id={editingId}
          onClose={() => setModalOpen(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
